import { useCallback, useRef } from 'react';
import { useLocale } from '@hooks/useLocale';
import { useCartUI } from '@context/CartContext';
import {
  AtcSource,
  getCartVariantIds,
  getVariantQuantity,
  pruneAtcSources,
  recordAtcSource,
} from '@utils/cart/atcSource';
import type { Cart, ItemBody } from '../../types/cart';
import { HookError } from '../../errorHandlers';

export const addItemFetcher = async (
  item: ItemBody,
  locale: string
): Promise<Cart> => {
  if (
    item.quantity !== undefined &&
    (!Number.isInteger(item.quantity) || item.quantity < 1)
  ) {
    throw new HookError(
      'The item quantity has to be a valid integer greater than 0'
    );
  }

  const addItemResponse = await fetch('/api/cart', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept-Language': locale,
    },
    body: JSON.stringify({ item }),
  });

  const { data, errors } = await addItemResponse.json();

  // The route falls back to `res.status(500).json({ data: null, errors })`
  // for any upstream failure it has no specific 422 handler for — without
  // this check a null cart silently reads as "nothing went wrong", and
  // callers (Quick Add, AddToCartCTA) would treat the add as successful.
  if (!addItemResponse.ok || !data) {
    throw new HookError(errors?.[0]?.message ?? 'Failed to add item to cart');
  }

  return data;
};

export type OnAddItemCallback = ({
  data,
  item,
}: {
  data: Cart;
  item: ItemBody;
}) => void;

const useAddItem = (onAddItem: OnAddItemCallback) => {
  const { cart, updateCart } = useCartUI();
  // Read at call time without making `cart` a dependency: callers keep a stable
  // `addItem` identity across cart updates.
  const cartRef = useRef(cart);
  cartRef.current = cart;
  const locale = useLocale();
  if (!locale)
    throw new HookError('Locale must be specified to add item to cart');

  return useCallback(
    /**
     * `atcSource` stays out of `input` (which is sent to BigCommerce) and is recorded
     * client-side (ED-555) only when the variant's quantity actually went up. A
     * resolved request isn't enough: `/api/cart` answers some errors with a 200 and
     * a cart, and its stock fallback can leave the line unchanged — neither may
     * overwrite the source of the last add that really happened. A real add with no
     * source clears the variant's previous one.
     */
    async (input: ItemBody, atcSource?: AtcSource) => {
      const previousQuantity = getVariantQuantity(
        cartRef.current?.cartData?.line_items,
        input.variantId
      );
      const data = await addItemFetcher(input, locale);
      updateCart(data);
      try {
        const lineItems = data?.cartData?.line_items;
        const cartVariantIds = lineItems
          ? getCartVariantIds(lineItems)
          : undefined;

        if (getVariantQuantity(lineItems, input.variantId) > previousQuantity) {
          recordAtcSource(input.variantId, atcSource, cartVariantIds);
        } else if (cartVariantIds) {
          pruneAtcSources(cartVariantIds);
        }
      } catch (error) {
        console.log(error);
      }
      try {
        onAddItem?.({ data, item: input });
      } catch (error) {
        console.log(error);
      }
    },
    [locale, updateCart, onAddItem]
  );
};

export default useAddItem;
